// Import necessary modules/components
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeleteModal from "../../components/Common/DeleteModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios";
import { handleAuthError, toBase64Url, useThousand } from "../../utils";
import withAuth from "../withAuth";
import CountrySelect from "../CountrySelect";
import { baseUrl } from '../../Network';

import {
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Tooltip,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { calculateDebt } from "../../utils";
import PaginationControls from "../../components/Common/PaginationControls";
import ActionsDropdown from "../../components/Common/ActionsDropdown";
import { useConfirm } from "../../contexts/ConfirmContext";
import { isMarketerReadOnly } from "../../helpers/adminRole";
import PartnerReadOnlyBanner from "../../components/Common/PartnerReadOnlyBanner";

const ManagePartner = () => {
  const readOnly = isMarketerReadOnly();
  const PARTNER_SIGNUP_URL = "https://partners.rydlearning.com/signup";
  const { showConfirm } = useConfirm();
  const [partners, setPartners] = useState([]);
  const [partner, setPartner] = useState({});
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isApprove, setIsApprove] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [showAll, setShowAll] = useState(false);
  const [teacherPartner, setTeacherPartner] = useState(null);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [teacherSaving, setTeacherSaving] = useState(false);

  const openTeacherAssign = async (partnerRow) => {
    setTeacherPartner(partnerRow);
    setTeacherOptions([]);
    setSelectedTeacherIds([]);
    try {
      const [allTeachers, assigned] = await Promise.all([
        axios.get(`${baseUrl}/admin/teacher/assignable`),
        axios.get(`${baseUrl}/admin/partner/teachers/${partnerRow.id}`),
      ]);
      const options = Array.isArray(allTeachers.data?.data) ? allTeachers.data.data : [];
      const current = Array.isArray(assigned.data?.data) ? assigned.data.data : [];
      setTeacherOptions(options);
      setSelectedTeacherIds(current.map((teacher) => Number(teacher.id)));
    } catch (error) {
      handleAuthError(error);
      toast.error(error?.response?.data?.message || "Unable to load teachers");
    }
  };

  const savePartnerTeachers = async () => {
    if (!teacherPartner) return;
    setTeacherSaving(true);
    try {
      await axios.put(`${baseUrl}/admin/partner/teachers/${teacherPartner.id}`, {
        teacherIds: selectedTeacherIds,
      });
      toast.success("Teachers assigned");
      setTeacherPartner(null);
    } catch (error) {
      handleAuthError(error);
      toast.error(error?.response?.data?.message || "Unable to assign teachers");
    } finally {
      setTeacherSaving(false);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      currency: partner?.currency || "",
      amount: partner?.amount || ""
    },
    validationSchema: Yup.object({
      currency: Yup.string().required("Please Enter Currency"),
      amount: Yup.number().required("Please Enter Amount")
    }),

    onSubmit: async (values) => {
      setLoading(true);
      try {
        const partnerData = {
          currency: values.currency,
          amount: values.amount,
          id: partner.id
        };

        let endpoint = isApprove
          ? `${baseUrl}/admin/partner/approve`
          : `${baseUrl}/admin/partner/update`;

        let response = await axios.put(endpoint, partnerData);
        toast.success(isApprove ? "Partner approved successfully" : "Partner updated successfully");

        fetchPartners()
        toggle();
      } catch (error) {
        handleAuthError(error);
        toast.error(error.response?.data?.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    window.document.title = "Partner - RYD Admin";
    fetchPartners();
  }, [currentPage, pageSize, searchQuery]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const limit = showAll ? 10000 : pageSize;
      const params = {
        page: currentPage,
        limit: limit,
        ...(searchQuery && { search: searchQuery })
      };
      const response = await axios.get(`${baseUrl}/admin/partner/all`, { params });
      const responseData = response.data.data;
      const data = Array.isArray(responseData) ? responseData : (responseData?.data || []);
      const paginationData = responseData?.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: Array.isArray(data) ? data.length : 0,
        itemsPerPage: limit,
        hasNextPage: false,
        hasPreviousPage: false
      };
      setPartners(Array.isArray(data) ? data : []);
      setPagination(paginationData);
      setLoading(false);
    } catch (error) {
      handleAuthError(error);
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    if (size === 'all') {
      setShowAll(true);
      setPageSize(10000);
    } else {
      setShowAll(false);
      setPageSize(parseInt(size));
      setCurrentPage(1);
    }
  };

  const goToNextPage = () => {
    if (pagination.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (pagination.hasPreviousPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const openPartnerSignup = () => {
    window.open(PARTNER_SIGNUP_URL, "_blank", "noopener,noreferrer");
  };

  const copyPartnerSignupLink = async () => {
    try {
      await navigator.clipboard.writeText(PARTNER_SIGNUP_URL);
      toast.success("Partner signup link copied");
    } catch (e) {
      const textarea = document.createElement("textarea");
      textarea.value = PARTNER_SIGNUP_URL;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success("Partner signup link copied");
    }
  };

  const toggle = () => {
    setModal(!modal);
  };

  const handleCreateDiscountClick = (partnerData) => {
    setPartner(partnerData);
    setIsApprove(true);
    toggle();
  };

  const handleUpdateDiscountClick = (partnerData) => {
    setPartner(partnerData);
    setIsApprove(false);
    toggle();
  };

  const handleDisablePartner = async (id) => {
    setLoading(true)
    try {
      const response = await axios.put(`${baseUrl}/admin/partner/disable/${id}`);
      if (!response.status) {
        toast.error(response.message);
        return;
      }
      toast.success("Partner disabled successfully");
      const responseData = response.data;
      const updatedPartners = partners.map((p) =>
        p.id === partner.id ? { ...p, ...responseData } : p
      );
      setPartners(updatedPartners);
      fetchPartners()
    } catch (error) {
      handleAuthError(error)
      toast.error("Failed to disable partner");
    }
  };

  const handleEnablePartner = async (id) => {
    setLoading(true)
    try {
      const response = await axios.put(`${baseUrl}/admin/partner/enable/${id}`);
      if (!response.status) {
        toast.error(response.message);
        return;
      }
      toast.success("Partner enabled successfully");
      const responseData = response.data;
      const updatedPartners = partners.map((p) =>
        p.id === partner.id ? { ...p, ...responseData } : p
      );
      setPartners(updatedPartners);
      fetchPartners()
    } catch (error) {
      handleAuthError(error)
      toast.error("Failed to enable partner");
    }
  };

  const token = localStorage.getItem('token');

  function openPartnerLogin(partnerId) {
    const payload = {
      adminToken: token,
      partnerId,
      timestamp: Date.now(),
    };

    const code = toBase64Url(payload);
    // const url = "http://localhost:5173"
    const url = "https://partners.rydlearning.com"
    const partnerUrl = `${url}/auth/redirect?code=${encodeURIComponent(code)}`;

    window.open(partnerUrl, "_blank", "noopener,noreferrer");
  }

  async function openAiTutor() {
    try {
      const res = await axios.post(`${baseUrl}/admin/ai/explore`, {});
      if (res?.data?.status && res?.data?.data?.url) {
        window.open(res.data.data.url, "_blank", "noopener,noreferrer");
        toast.success("Opening AI Tutor...");
        return;
      }
      toast.error(res?.data?.message || "Unable to open AI Tutor");
    } catch (error) {
      handleAuthError(error);
      toast.error("Unable to open AI Tutor");
    }
  }


  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Manage Partners" />
          {readOnly && <PartnerReadOnlyBanner />}
          <Row>
            <Col lg="12">
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="mb-3">
                    <h5 className="card-title">
                      Partner List{" "}
                      <span className="text-muted fw-normal ms-2">
                        ({partners.length})
                      </span>
                    </h5>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3 p-3 bg-light rounded filter-bar">
                    <div>
                      <Input
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={handleSearch}
                      />
                    </div>
                    <div>
                      <button
                        className="btn btn-light d-none"
                        onClick={() => {
                          setPartner({});
                          setIsEdit(false);
                          toggle();
                        }}>
                        <i className="bx bx-plus me-1"></i> Add New Partner
                      </button>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xl="12" className={'divTable'}>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                    <span className="text-muted">Partner signup link:</span>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <a
                        href={PARTNER_SIGNUP_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary text-break"
                      >
                        {PARTNER_SIGNUP_URL}
                      </a>
                      <Button type="button" color="light" size="sm" onClick={copyPartnerSignupLink}>
                        <i className="mdi mdi-content-copy me-1" />
                        Copy
                      </Button>
                      <Button type="button" color="primary" size="sm" onClick={openPartnerSignup}>
                        <i className="mdi mdi-open-in-new me-1" />
                        Open
                      </Button>
                    </div>
                  </div>
                  {loading ? (
                    <div className="text-center mt-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : Array.isArray(partners) && partners.length === 0 ? (
                    <div className="text-center mt-5">
                      <h3>No data available</h3>
                    </div>
                  ) : (
                    <div className="table-responsive">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Organization Name</th>
                          <th>Address</th>
                          <th>email</th>
                          <th>Country</th>
                          <th>Private lesson teachers</th>
                          <th>Currency</th>
                          <th>Amount</th>
                          <th>Debt</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partners.map((p, index) => {
                          const globalIndex = showAll 
                            ? index 
                            : (pagination.currentPage - 1) * pagination.itemsPerPage + index;
                          return (
                            <tr key={p.id || index}>
                              <td>{globalIndex + 1}</td>
                            <td>{p?.organizationName}</td>
                            <td>{p?.address}</td>
                            <td>{p?.email}</td>
                            <td>{p?.country}</td>
                            <td>{(p?.partnerTeachers || []).map((link) => [link.teacher?.firstName, link.teacher?.lastName].filter(Boolean).join(" ")).filter(Boolean).join(", ") || "—"}</td>
                            <td>{p?.currency}</td>
                            <td>{useThousand(p?.amount)}</td>
                            <td>{p?.currency}{useThousand(calculateDebt(p?.programs, p?.amount))}</td>
                            <td>{p?.approved ? "Approved" : "Unapproved"}</td>
                            <td>
                              {readOnly ? (
                                <Link
                                  to={`/partner/dashboard/${p.id}`}
                                  className="btn btn-sm btn-primary"
                                >
                                  <i className="mdi mdi-eye me-1" />
                                  View Partner
                                </Link>
                              ) : (
                                <ActionsDropdown
                                  loading={actionLoadingId === p.id}
                                  toggleTitle="Partner actions"
                                  items={[
                                    ...(p.approved ? [{ label: "Edit", icon: "mdi-pencil", onClick: () => handleUpdateDiscountClick(p), variant: "success" }] : []),
                                    p.status
                                      ? { label: "Restrict account", icon: "mdi-lock", onClick: () => showConfirm({ title: "Restrict account", message: "Do you really want to DISABLE " + (p?.organizationName || "this partner") + "?", variant: "danger" }).then(async (ok) => { if (ok) { setActionLoadingId(p.id); try { await handleDisablePartner(p.id); } finally { setActionLoadingId(null); } } }), variant: "danger" }
                                      : { label: "Remove restriction", icon: "mdi-lock-open", onClick: () => showConfirm({ title: "Remove restriction", message: "Do you really want to ENABLE " + (p?.organizationName || "this partner") + "?", variant: "primary" }).then(async (ok) => { if (ok) { setActionLoadingId(p.id); try { await handleEnablePartner(p.id); } finally { setActionLoadingId(null); } } }), variant: "success" },
                                    ...(p.approved
                                      ? [
                                          { label: "Manage Partner", icon: "mdi-cog", href: `/partner/dashboard/${p.id}` },
                                          { label: "Assign teachers", icon: "mdi-account-tie", onClick: () => openTeacherAssign(p) },
                                          { label: "Manage access", icon: "mdi-shield-key", href: `/partner/manage-access/${p.id}` },
                                      ]
                                      : [{ label: "Approve", icon: "mdi-check", onClick: () => handleCreateDiscountClick(p) }]),
                                    { divider: true },
                                    { label: "Explore account", icon: "mdi-open-in-new", onClick: () => showConfirm({ title: "Explore account", message: "Do you really want to explore " + (p?.organizationName || "this partner") + "'s account?", variant: "primary" }).then(async (ok) => { if (ok) { setActionLoadingId(p.id); try { await openPartnerLogin(p.id); } finally { setActionLoadingId(null); } } }), variant: "success" },
                                    { label: "Explore AI Tutor", icon: "mdi-brain", onClick: () => showConfirm({ title: "Explore AI Tutor", message: "Open AI Tutor as admin now?", variant: "primary" }).then(async (ok) => { if (ok) { setActionLoadingId(p.id); try { await openAiTutor(); } finally { setActionLoadingId(null); } } }), variant: "info" },
                                  ]}
                                />
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  )}
                  {!loading && Array.isArray(partners) && partners.length > 0 && (
                    <PaginationControls
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      totalItems={pagination.totalItems}
                      pageSize={pagination.itemsPerPage}
                      showAll={showAll}
                      handlePageChange={handlePageChange}
                      handlePageSizeChange={handlePageSizeChange}
                      goToNextPage={goToNextPage}
                      goToPreviousPage={goToPreviousPage}
                      canPreviousPage={pagination.hasPreviousPage}
                      canNextPage={pagination.hasNextPage}
                      paginatedDataLength={partners.length}
                    />
                  )}
                  <Modal isOpen={modal} toggle={toggle}>
                    <ModalHeader toggle={toggle}>
                      {isApprove ? "Approve Partner" : "Edit Partner"}
                    </ModalHeader>
                    <ModalBody>
                      <Form onSubmit={validation.handleSubmit}>
                        <Row>
                          <Col xs={12}>
                            <div className="mb-3">
                              <Label className="form-label">Currency</Label>
                              <Input
                                name="currency"
                                type="select"  // Change type to "select"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.currency}
                                invalid={
                                  validation.touched.currency &&
                                  validation.errors.currency
                                }
                              >
                                <option value="">Select Currency</option>
                                <option value="CAD">CAD</option>
                                <option value="NGN">NGN</option>
                              </Input>
                              <FormFeedback type="invalid">
                                {validation.errors.currency}
                              </FormFeedback>
                            </div>
                          </Col>
                          <Col xs={12}>
                            <div className="mb-3">
                              <Label className="form-label">Amount</Label>
                              <Input
                                name="amount"
                                type="number"
                                placeholder="Enter amount"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.amount}
                                invalid={
                                  validation.touched.amount &&
                                  validation.errors.amount
                                }
                              />
                              <FormFeedback type="invalid">
                                {validation.errors.amount}
                              </FormFeedback>
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <div className="text-end">
                              <button
                                type="submit"
                                disabled={loading}
                                className="btn btn-primary save-user"
                              >
                                {loading ? 'Processing...' : (isApprove ? 'Approve' : 'Update')}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </ModalBody>
                  </Modal>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
      <Modal isOpen={Boolean(teacherPartner)} toggle={() => setTeacherPartner(null)} centered>
        <ModalHeader toggle={() => setTeacherPartner(null)}>
          Teachers for {teacherPartner?.organizationName}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">These teachers are the only ones this partner can assign to a private lesson.</p>
          <div className="d-flex flex-column gap-2" style={{ maxHeight: 320, overflowY: "auto" }}>
            {teacherOptions.length === 0 ? (
              <p className="text-muted mb-0">No active teachers to assign.</p>
            ) : teacherOptions.map((teacher) => {
              const teacherId = Number(teacher.id);
              const selected = selectedTeacherIds.includes(teacherId);
              return (
                <div
                  key={teacherId}
                  role="checkbox"
                  aria-checked={selected}
                  tabIndex={0}
                  className="d-flex align-items-center gap-2 py-1"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedTeacherIds((current) => (
                      current.includes(teacherId)
                        ? current.filter((id) => id !== teacherId)
                        : [...current, teacherId]
                    ));
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== " " && event.key !== "Enter") return;
                    event.preventDefault();
                    setSelectedTeacherIds((current) => (
                      current.includes(teacherId)
                        ? current.filter((id) => id !== teacherId)
                        : [...current, teacherId]
                    ));
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    readOnly
                    tabIndex={-1}
                    style={{ pointerEvents: "none" }}
                  />
                  <span>{teacher.firstName} {teacher.lastName}</span>
                </div>
              );
            })}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button color="light" onClick={() => setTeacherPartner(null)}>Cancel</Button>
            <Button color="primary" disabled={teacherSaving} onClick={savePartnerTeachers}>
              {teacherSaving ? "Saving…" : "Save teachers"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(ManagePartner);